import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCourse } from '../context/CourseContext';
import { courseInfo, testimonials, fourPillars, courseIntro } from '../data/courseContent';
import { START_HERE_TRACK, BONUS_TRACKS } from '../data/courseMeditations';
import { MeditationPlayer } from '../components/course/MeditationPlayer';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  ArrowRight,
  BookOpen,
  Target,
  Heart,
  Sparkles,
  CheckCircle2,
  Quote,
  Play,
  ChevronDown,
  Brain,
  Zap,
  Shield,
  Rocket,
  Headphones,
} from 'lucide-react';

export const HomePage = () => {
  const { chapters, overallProgress, getChapterProgress } = useCourse();

  const pillarIcons = [Target, Brain, Shield, Rocket];

  const scrollToContent = () => {
    document.getElementById('content-start')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-gradient overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={courseInfo.heroImage}
            alt="Transformation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/80 via-primary-dark/60 to-maroon/20" />
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 right-20 w-64 h-64 rounded-full bg-silver/10 blur-3xl" />
        <div className="absolute bottom-20 left-20 w-48 h-48 rounded-full bg-secondary/15 blur-3xl" />
        <div className="absolute top-40 left-1/3 w-32 h-32 rounded-full bg-maroon/15 blur-2xl" />
        <div className="absolute bottom-40 right-1/4 w-40 h-40 rounded-full bg-silver-shimmer/20 blur-2xl" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-maroon/30 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-maroon-light/30">
                <BookOpen className="w-4 h-4 text-silver-light" />
                <span className="text-sm text-silver-light font-medium">
                  The Magnetic Mind Method
                </span>
              </div>

              {/* Title */}
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground mb-4 leading-tight">
                Taking Your{' '}
                <span className="text-gradient-gold">Power</span>{' '}
                Back
              </h1>
              
              {/* Presented By */}
              <p className="text-base text-secondary mb-6">
                Presented by Breah Kettunen
              </p>

              {/* Subtitle */}
              <p className="text-lg sm:text-xl text-primary-foreground/80 mb-8 leading-relaxed max-w-2xl">
                {courseInfo.description}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link to={overallProgress > 0 ? `/chapter/${chapters.find(ch => !getChapterProgress(ch.id).completed)?.id || 1}` : '/chapter/1'}>
                  <Button size="lg" className="btn-gold text-lg px-8 py-6 gap-3">
                    {overallProgress > 0 ? (
                      <>
                        Continue Journey
                        <ArrowRight className="w-5 h-5" />
                      </>
                    ) : (
                      <>
                        Begin Your Transformation
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </Button>
                </Link>
                <Link to="/chapters">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="text-lg px-8 py-6 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
                  >
                    View All Chapters
                  </Button>
                </Link>
              </div>

              {/* Progress (if started) */}
              {overallProgress > 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="bg-primary-foreground/10 backdrop-blur-sm rounded-lg p-4 max-w-md"
                >
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-primary-foreground/80">Your Progress</span>
                    <span className="text-secondary font-semibold">{overallProgress}% Complete</span>
                  </div>
                  <Progress value={overallProgress} className="h-2" />
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.button
          onClick={scrollToContent}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 cursor-pointer"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="scroll-indicator">
            <div className="scroll-dot" />
          </div>
        </motion.button>
      </section>

      {/* This Course is For You */}
      <section id="content-start" className="py-20 bg-gradient-to-b from-background to-silver-shimmer/10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <div className="maroon-accent mx-auto mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
              This Course is for You if...
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto mb-16">
            <div className="grid gap-4">
              {courseIntro.points.map((point, index) => (
                <motion.div
                  key={`intro-point-${index}`}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-4 bg-card p-4 rounded-lg border border-silver/20 shadow-sm hover:shadow-silver transition-shadow"
                >
                  <CheckCircle2 className="w-6 h-6 text-maroon flex-shrink-0 mt-0.5" />
                  <p className="text-foreground">{point}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* 4 Pillars */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto mb-12"
          >
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
              The 4 Pillars You Need to Know
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {fourPillars.map((pillar, index) => {
              const IconComponent = pillarIcons[index];
              return (
                <motion.div
                  key={pillar.number}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="pillar-card h-full hover:border-maroon/40">
                    <div className="w-12 h-12 rounded-full bg-maroon/20 flex items-center justify-center mx-auto mb-4 shadow-maroon">
                      <span className="font-display font-bold text-maroon text-lg">{pillar.number}</span>
                    </div>
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                      {pillar.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {pillar.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What You Will Learn */}
      <section className="py-20 bg-gradient-to-b from-silver-shimmer/10 to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto mb-12"
          >
            <div className="gold-accent mx-auto mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
              What You Will Learn
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto mb-12">
            {courseIntro.whatYouWillLearn.map((item, index) => (
              <motion.div
                key={`learn-item-${index}`}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4 mb-4"
              >
                <div className="w-8 h-8 rounded-full bg-maroon flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <p className="text-lg text-foreground pt-1">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Audio Companion — Start Here */}
      <section className="py-16 bg-gradient-to-b from-background to-silver-shimmer/10" data-testid="course-start-here-section">
        <div className="container mx-auto px-4 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <Badge variant="outline" className="border-secondary/30 text-secondary mb-3">
              <Headphones className="w-3 h-3 mr-1.5 inline" /> Audio Companion
            </Badge>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-3">
              Start Here
            </h2>
            <p className="text-muted-foreground text-base max-w-xl mx-auto">
              Begin every day with this guided meditation. Anchor your nervous system in your magnetic state before you open the book.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <MeditationPlayer tracks={[START_HERE_TRACK]} title="Daily Power Planning Practice" subtitle="A morning ritual with an evening check-in. Use this every day of the course." />
          </motion.div>
        </div>
      </section>

      {/* Chapter Preview */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto mb-12"
          >
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
              The 5 Steps of Creation
            </h2>
            <p className="text-muted-foreground text-lg">
              A complete roadmap for reclaiming your authentic self
            </p>
          </motion.div>

          {/* Steps Timeline */}
          <div className="max-w-4xl mx-auto">
            {chapters.map((chapter, index) => {
              const progress = getChapterProgress(chapter.id);
              return (
                <motion.div
                  key={chapter.id}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative flex items-start gap-6 pb-12 last:pb-0"
                >
                  {/* Timeline Line */}
                  {index < chapters.length - 1 && (
                    <div className="absolute left-6 top-12 w-0.5 h-full -translate-x-1/2 bg-border" />
                  )}

                  {/* Step Number */}
                  <div className={`chapter-badge flex-shrink-0 ${
                    progress.completed ? 'complete' : progress.started ? 'active' : 'default'
                  }`}>
                    {progress.completed ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      chapter.id
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 bg-card rounded-xl p-6 border shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                          {chapter.step}
                        </span>
                        <h3 className="font-display text-xl font-semibold text-foreground mt-1 mb-2">
                          {chapter.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {chapter.summary}
                        </p>
                      </div>
                      <Link to={`/chapter/${chapter.id}`}>
                        <Button size="sm" variant="outline" className="gap-1">
                          {progress.completed ? 'Review' : progress.started ? 'Continue' : 'Start'}
                          <ArrowRight className="w-3 h-3" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/chapters">
              <Button size="lg" className="btn-teal gap-2">
                Explore All Chapters
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Bonus Practices */}
      <section className="py-16 bg-background" data-testid="course-bonus-practices-section">
        <div className="container mx-auto px-4 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <Badge variant="outline" className="border-secondary/30 text-secondary mb-3">
              <Sparkles className="w-3 h-3 mr-1.5 inline" /> Bonus Practices
            </Badge>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-3">
              For deeper integration
            </h2>
            <p className="text-muted-foreground text-base max-w-xl mx-auto">
              Use these when you&apos;re ready to go deeper — or when life invites you to. Each is a standalone practice you can return to anytime.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <MeditationPlayer
              tracks={BONUS_TRACKS}
              title="4 Bonus Meditations"
              subtitle="Pick the one your nervous system is asking for."
            />
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-b from-background to-silver-shimmer/10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto mb-12"
          >
            <div className="silver-accent mx-auto mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Transformations That Inspire
            </h2>
            <p className="text-muted-foreground text-lg">
              Real stories from people who&apos;ve taken their power back
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border border-silver/20 shadow-sm hover:shadow-silver transition-shadow">
                  <CardContent className="p-6 flex flex-col h-full">
                    <Quote className="w-8 h-8 text-maroon/40 mb-4" />
                    <p className="text-foreground leading-relaxed flex-1 mb-6">
                      &ldquo;{testimonial.quote}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10 bg-maroon">
                        <AvatarFallback className="text-white font-semibold">
                          {testimonial.avatar}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-foreground">{testimonial.name}</p>
                        <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-dark via-primary to-maroon" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/60 to-maroon/40" />
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-secondary/15 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-silver/10 blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-primary-foreground mb-6">
              Ready to Transform Your Life?
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              You&apos;re not broken. You never were. It&apos;s time to remember your power.
            </p>
            <Link to="/chapter/1">
              <Button size="lg" className="btn-gold text-lg px-10 py-6 gap-3">
                Start Chapter 1
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
